import classNames from 'classnames';
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { usePathname, useRouter } from 'next/navigation';
import type { CSSProperties, ReactNode } from 'react';
import { slugify } from 'transliteration';

import NotificationsBadge from '~/shared/components/header/components/notifications-badge';
import StudentsAttentionBadge from '~/shared/components/students-attention-badge';
import Link from '~/shared/components/link';
import { type Locale, PAGE_NAMES, USER_ROLE } from '~/shared/constants';
import { getLocaleLink } from '~/shared/helpers/browser-helpers';
import { checkIsClientsPanel } from '~/shared/helpers/check-helpers';
import { changeAuthStatus } from '~/shared/helpers/cookie-helpers';
import { pushMenuItemIfAllowed } from '~/shared/permissions/helpers/push-menu-item-if-allowed';
import { getNavigationAccessConfig } from '~/shared/permissions/navigation-access';
import { P } from '~/shared/permissions/permissions';
import type { AppDispatch } from '~/shared/store';
import type { FrontendI18nKey, TypedTranslate } from '~/shared/types/i18n-type';
import type { ShortUserType, UserType } from '~/types';

export type UserMenuItem = Readonly<{
  key: string;
  label: ReactNode;
  href?: string;
  activePaths?: string[];
  excludePaths?: string[];
  className?: string;
  style?: CSSProperties;
}>;

type GetUserMenuItemsParams = Readonly<{
  user?: ShortUserType | null;
  currentUser?: UserType | null;
  isAuthenticated: boolean;
  lng: Locale;
  t: TypedTranslate;
  router: AppRouterInstance;
  onMobileMenuClose?: () => void;
  closeMenu?: () => void;
  onSignOut?: (() => void) | null;
  isClientsPanel?: boolean;
  isAdminPanel?: boolean;
  isPlatform?: boolean;
  dispatch?: AppDispatch;
  rootContext?: Record<string, unknown>;
}>;

const getUserMenuItems = ({
  user = null,
  isAuthenticated,
  closeMenu = () => {},
  currentUser,
  lng,
  isClientsPanel,
  onMobileMenuClose,
  onSignOut = null,
  isAdminPanel = false,
  isPlatform = false,
  t,
  rootContext = {},
  router,
}: GetUserMenuItemsParams): UserMenuItem[] => {
  const handleMenuClose = () => {
    onMobileMenuClose?.();
  };

  const handleSignOut = async () => {
    if (onSignOut) {
      onSignOut();
    } else {
      changeAuthStatus({
        isSignOut: true,
        isStayOnPage: false,
        rootContext,
        router,
        lng,
        isClientsPanel,
      });
    }
    handleMenuClose();
  };

  const { profileLink, dialogsLink, profilePermission, dialogsPermission } = getNavigationAccessConfig({
    lng,
    isPlatform,
    isAdminPanel,
  });
  const platformItems: UserMenuItem[] = [];

  if (!isClientsPanel) {
    pushMenuItemIfAllowed({
      items: platformItems,
      user,
      permission: P.ADMIN_PANEL_VIEW,
      item: {
        label: (
          <Link
            className="menu-bar__link"
            itemProp="url"
            onClick={handleMenuClose}
            href={getLocaleLink(PAGE_NAMES.ADMIN, lng)}
          >
            {t('page.admin.menu-item')}
          </Link>
        ),
        className: 'menu-bar__sub-item',
        key: getLocaleLink(PAGE_NAMES.ADMIN, lng),
        activePaths: [getLocaleLink(PAGE_NAMES.ADMIN, lng)],
        excludePaths: [
          getLocaleLink(PAGE_NAMES.ADMIN_USER_PROFILE, lng),
          getLocaleLink(PAGE_NAMES.ADMIN_USER_DIALOGS, lng),
        ],
      },
    });

    // ONLINE COURSES
    pushMenuItemIfAllowed({
      items: platformItems,
      user,
      permission: P.PLATFORM_COURSES_VIEW,
      item: {
        label: (
          <Link
            className="menu-bar__link"
            itemProp="url"
            onClick={handleMenuClose}
            href={getLocaleLink(PAGE_NAMES.PLATFORM_COURSES, lng)}
          >
            {t('page.platform.online-courses.menu-item')}
          </Link>
        ),
        className: 'menu-bar__sub-item',
        key: getLocaleLink(PAGE_NAMES.PLATFORM_COURSES, lng),

        activePaths: [
          getLocaleLink(PAGE_NAMES.PLATFORM_COURSES, lng),
          getLocaleLink(PAGE_NAMES.PLATFORM_ONLINE_COURSE_WITHOUT_ALIAS, lng),
        ],

        // ‚Äö√Ñ√¨¬¨‚Ä¢‚Äö√Ñ√¨‚àö¬∂‚Äö√Ñ√¨‚àö‚àè‚Äö√Ñ√¨‚àö¬∂‚Äö√Ñ√¨¬¨‚Ñ¢‚Äö√Ñ√¨≈í¬©‚Äö√Ñ√¨‚Äö√†√®‚Äö√Ñ√Æ‚àö√°‚Äö√Ñ√¨¬¨¬µ‚Äö√Ñ√¨¬¨‚Ñ¢‚Äö√Ñ√Æ‚àö‚Ä¢‚Äö√Ñ√¨≈í¬©‚Äö√Ñ√¨‚Äö√†√ª‚Äö√Ñ√Æ‚àö¬Æ ‚Äö√Ñ√¨‚Äö√†√´‚Äö√Ñ√¨‚Äö√†√ª‚Äö√Ñ√Æ‚àö¬¢‚Äö√Ñ√¨‚Äö√†√®‚Äö√Ñ√Æ‚àö√°‚Äö√Ñ√¨‚Äö√†√ª ‚Äö√Ñ√¨‚àö¬∂‚Äö√Ñ√Æ‚àö√° ‚Äö√Ñ√¨‚àö‚àè‚Äö√Ñ√¨¬¨¬µ‚Äö√Ñ√Æ‚àö√ë‚Äö√Ñ√¨¬¨¬µ‚Äö√Ñ√Æ‚àö√ñ‚Äö√Ñ√¨¬¨¬µ‚Äö√Ñ√Æ‚àö¬∞‚Äö√Ñ√¨¬¨¬µ‚Äö√Ñ√¨≈í¬©‚Äö√Ñ√¨‚Äö√†√®‚Äö√Ñ√Æ‚àö¬Æ ‚Äö√Ñ√¨¬¨‚à´‚Äö√Ñ√¨‚Äö√†√ª‚Äö√Ñ√Æ‚àö√ë‚Äö√Ñ√Æ‚àö‚Ä†‚Äö√Ñ√Æ‚àö√ë‚Äö√Ñ√Æ‚àö√¢‚Äö√Ñ√Æ‚àö√°‚Äö√Ñ√¨‚àö¬∂‚Äö√Ñ√¨‚Äö√¢¬ß
        excludePaths: [
          getLocaleLink(PAGE_NAMES.PLATFORM_VIDEO_COURSES, lng),
          getLocaleLink(PAGE_NAMES.PLATFORM_VIDEO_COURSE_WITHOUT_ALIAS, lng),
        ],
      },
    });

    pushMenuItemIfAllowed({
      items: platformItems,
      user,
      permission: P.PLATFORM_VIDEO_COURSES_VIEW,
      item: {
        label: (
          <Link
            className="menu-bar__link"
            itemProp="url"
            onClick={handleMenuClose}
            href={getLocaleLink(PAGE_NAMES.PLATFORM_VIDEO_COURSES, lng)}
          >
            {t('page.platform.video-courses.menu-item')}
          </Link>
        ),
        className: 'menu-bar__sub-item',
        key: getLocaleLink(PAGE_NAMES.PLATFORM_VIDEO_COURSES, lng),

        activePaths: [
          getLocaleLink(PAGE_NAMES.PLATFORM_VIDEO_COURSES, lng),
          getLocaleLink(PAGE_NAMES.PLATFORM_VIDEO_COURSE_WITHOUT_ALIAS, lng),
        ],
      },
    });

    pushMenuItemIfAllowed({
      items: platformItems,
      user,
      permission: P.PLATFORM_STUDENTS_LIST_VIEW,
      item: {
        label: (
          <Link
            className="menu-bar__link"
            itemProp="url"
            onClick={handleMenuClose}
            href={getLocaleLink(PAGE_NAMES.PLATFORM_STUDENTS_LIST, lng)}
          >
            <span className="menu-bar__students-attention-label">
              <span>{t('page.platform.students-board.menu-item')}</span>
              {(user?.role === USER_ROLE.TEACHER || user?.role === USER_ROLE.SUPER_TEACHER) && (
                <StudentsAttentionBadge />
              )}
            </span>
          </Link>
        ),
        className: 'menu-bar__sub-item',
        key: getLocaleLink(PAGE_NAMES.PLATFORM_STUDENTS_LIST, lng),
        activePaths: [
          getLocaleLink(PAGE_NAMES.PLATFORM_STUDENTS_LIST, lng),
          getLocaleLink(PAGE_NAMES.PLATFORM_STUDENTS_BOARD, lng),
        ],
      },
    });

    pushMenuItemIfAllowed({
      items: platformItems,
      user,
      permission: profilePermission,
      item: {
        label: (
          <Link className="menu-bar__link" itemProp="url" onClick={handleMenuClose} href={profileLink}>
            {t('page.user-profile.menu-item')}
          </Link>
        ),
        className: 'menu-bar__sub-item',
        key: profileLink,
      },
    });

    pushMenuItemIfAllowed({
      items: platformItems,
      user,
      permission: dialogsPermission,
      item: {
        label: (
          <Link className="menu-bar__link" itemProp="url" onClick={handleMenuClose} href={dialogsLink}>
            {t('text.support')}
          </Link>
        ),
        className: 'menu-bar__sub-item',
        key: dialogsLink,
      },
    });
  }

  if (isAuthenticated) {
    platformItems.push({
      key: getLocaleLink(
        isClientsPanel ? PAGE_NAMES.CLIENTS_NOTIFICATIONS : PAGE_NAMES.PLATFORM_USER_NOTIFICATIONS,
        lng,
      ),
      label: (
        <Link
          className={classNames('menu-bar__link', 'menu-bar__notifications')}
          itemProp="url"
          onClick={handleMenuClose}
          href={getLocaleLink(
            isClientsPanel ? PAGE_NAMES.CLIENTS_NOTIFICATIONS : PAGE_NAMES.PLATFORM_USER_NOTIFICATIONS,
            lng,
          )}
        >
          <span className="menu-bar__notifications-title">{t('page.platform.notifications.menu-item')}</span>
          <span className="menu-bar__notifications-counter">
            <NotificationsBadge lng={lng} closeMenu={closeMenu} isAuthenticated={isAuthenticated} />
          </span>
        </Link>
      ),
      className: 'menu-bar__sub-item',
    });
    platformItems.push({
      label: (
        <span className="menu-bar__link" onClick={handleSignOut}>
          {t('page.sign-out.menu-item')}
        </span>
      ),
      className: 'menu-bar__sub-item',
      key: 'sign-out',
    });

    return [
      {
        label: (
          <div className="menu-bar__user">
            <div className="menu-bar__user-role">
              {t(
                `text.user-role-${slugify(user?.role ?? '', {
                  lowercase: true,
                  replace: {
                    _: '-',
                  },
                })}` as FrontendI18nKey,
              )}
            </div>
            <div className="menu-bar__user-name">
              {user?.firstName}&nbsp;{user?.lastName}
            </div>
          </div>
        ),
        className: 'menu-bar__user-wrapper',
        style: {
          height: 'auto',
        },
        key: 'user-name',
      },
      ...platformItems,
    ];
  }

  return [
    {
      label: (
        <Link
          className="menu-bar__link"
          itemProp="url"
          onClick={handleMenuClose}
          href={getLocaleLink(PAGE_NAMES.SIGN_IN, lng)}
        >
          {t('page.sign-in.menu-item')}
        </Link>
      ),
      className: 'menu-bar__sub-item',
      key: getLocaleLink(PAGE_NAMES.SIGN_IN, lng),
    },
    {
      label: (
        <Link
          className="menu-bar__link"
          itemProp="url"
          onClick={handleMenuClose}
          href={getLocaleLink(PAGE_NAMES.SIGN_UP, lng)}
        >
          {t('page.sign-up.menu-item')}
        </Link>
      ),
      className: 'menu-bar__sub-item',
      key: getLocaleLink(PAGE_NAMES.SIGN_UP, lng),
    },
  ];
};

export default getUserMenuItems;
